/**
 * Systems, at the left of the strip (Jeff, 2026-10-25): one line ("All systems go", or the piece
 * that needs a look) and, on hover or click, every piece with its dot, word and when. Calls to this
 * desk come from the socket; the rest from `overview.get`, which the desk already reads each minute.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { type LiveState, type LiveStatus } from '../../lib/live';
import { overviewQuery } from '../../lib/queries';
import { describeRpcError } from '../../lib/rpc';
import { callsRow, systemRows, systemsSummary, TONE_DOT, TONE_TEXT, type SystemRow } from '../../lib/systems';

export function SystemsMenu({ status, calls, now }: { status: LiveStatus; calls: LiveState['calls']; now?: Date }) {
  const q = useQuery(overviewQuery());
  const o = q.data;
  const rows: SystemRow[] = [callsRow(status, calls), ...(o ? systemRows(o.systems, o.ai, now ?? new Date()) : [])];
  // A failed read says so (2026-10-27: "Checking the rest" stayed up with the reason hidden).
  const failed = !o && q.isError ? `Could not check the rest: ${describeRpcError(q.error)}` : null;
  return <SystemsView rows={rows} loading={!o && !failed} failed={failed} />;
}

export function SystemsView({ rows, loading = false, failed = null }: { rows: SystemRow[]; loading?: boolean; failed?: string | null }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement>(null);
  // Hover opens it and leaving closes it; a click pins it open until a second click, Escape or a
  // click outside (2026-10-29). Before the pin, a pointer resting on the button lost the panel when
  // overview.get landed: the summary words changed, the button resized out from under the pointer,
  // and the browser's mouseleave shut it (the nightly e2e 05 failure, every night from Oct 26).
  const pinned = useRef(false);
  const summary = systemsSummary(rows);
  const close = () => {
    pinned.current = false;
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      ref={root}
      className="relative"
      onMouseEnter={() => {
        setOpen(true);
      }}
      onMouseLeave={() => {
        if (!pinned.current) setOpen(false);
      }}
    >
      <button
        type="button"
        aria-label={`Systems: ${summary.words}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          if (pinned.current) close();
          else {
            pinned.current = true;
            setOpen(true);
          }
        }}
        className={`inline-flex h-8 items-center gap-2 whitespace-nowrap rounded-control px-2 hover:bg-surface-2 ${TONE_TEXT[summary.tone]}`}
      >
        <span aria-hidden className={`size-2 shrink-0 rounded-full ${TONE_DOT[summary.tone]}`} />
        {summary.words}
        <ChevronDown aria-hidden className="size-3.5 text-ink-3" />
      </button>
      {open && (
        // pt-1 instead of a margin keeps the pointer inside the menu on its way down.
        <div className="absolute left-0 top-full z-30 pt-1">
          <div id={panelId} role="dialog" aria-label="Systems" className="w-[400px] rounded-panel border border-line bg-surface shadow-[var(--overlay-shadow)]">
            <div className="flex items-baseline gap-3 border-b border-line px-3 py-2">
              <span className="text-emphasis font-medium text-ink">Systems</span>
              <Link
                to="/settings/integrations"
                className="ml-auto text-hint text-info hover:underline"
                onClick={close}
              >
                Every sync in Settings
              </Link>
            </div>
            <ul className="divide-y divide-line">
              {rows.map((r) => (
                <li key={r.label} className="grid grid-cols-[136px_1fr] gap-x-3 px-3 py-2 text-body">
                  <span className="text-ink-2">{r.label}</span>
                  <span className="min-w-0">
                    <span className="inline-flex items-center gap-2 text-ink">
                      <span aria-hidden className={`size-2 shrink-0 rounded-full ${TONE_DOT[r.tone]}`} />
                      {r.word}
                    </span>
                    {r.when && <span className="block text-hint text-ink-3">{r.when}</span>}
                  </span>
                </li>
              ))}
              {loading && <li className="px-3 py-2 text-hint text-ink-3">Checking the rest</li>}
              {failed && <li className="px-3 py-2 text-hint text-alert">{failed}</li>}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
